import {Constants} from "../Constants";
import type {SpaceItemType} from "../managers/spaceitems/ItemManager";
import type {ITextPart} from "../managers/MSDF/TextUtils";
import type {BoundarySpaceMap} from "../../../../../../data/models/BoundarySpaceMap";
import type {Dimensions, PointDouble, SpaceFileInsertionInfo} from "../../../../../../generated/api/base";
import {FieldDataType, Permission, XyiconFeature} from "../../../../../../generated/api/base";
import type {IFieldAdapter} from "../../../../../../data/models/field/Field";
import type {IModel, ISpaceItemModel} from "../../../../../../data/models/Model";
import type {AppActions} from "../../../../../../data/state/AppActions";
import {HorizontalAlignment, VerticalAlignment} from "../../../../../../utils/dom/DomUtils";
import {PolyLabel} from "../../../../../../utils/math/PolyLabel";
import type {IRect} from "../../../../../../utils/THREEUtils";
import {THREEUtils} from "../../../../../../utils/THREEUtils";
import {VectorUtils} from "../../../../../../utils/VectorUtils";
import type {IMarkupMinimumData} from "../../../../../../data/models/Markup";
import type {Space} from "../../../../../../data/models/Space";
import type {IRealSize} from "../../../../settings/modules/type/form/RealSizeInput";
import {NotificationType} from "../../../../../notification/Notification";
import {notify} from "../../../../../../utils/Notify";
import {StringUtils} from "../../../../../../utils/data/string/StringUtils";
import type {SpaceViewRenderer} from "./SpaceViewRenderer";

export type ThemeType = "light" | "dark";

export interface ICaptionParent extends IRect {
	id: string;
	spaceItemType: "xyicon" | "boundary";
	geometryData?: PointDouble[];
}
export interface ICaption extends IRect {
	text: ITextPart[];
	parent: ICaptionParent;
}

export interface IRenderDebugSnapshot {
	calls: number;
	triangles: number;
	lines: number;
	points: number;
	geometries: number;
	textures: number;
	zoom: number;
}

export interface IRenderedFrameDebugSummary {
	fps: number;
	lastFrameMs: number | null;
	averageFrameMs: number | null;
	maxFrameMs: number | null;
	isIdle: boolean;
}

export enum SpaceEditorMode {
	NORMAL,
	SET_SCALE,
	ALIGN,
}

const RenderedFrameIdleThresholdMs = 350;

export const getRenderedFrameDebugSummary = (
	frameTimestamps: number[],
	frameDurationsMs: number[],
	now: number,
	idleThresholdMs: number = RenderedFrameIdleThresholdMs,
): IRenderedFrameDebugSummary => {
	const timestampsWithinLastSecond = frameTimestamps.filter((timestamp) => now - timestamp <= 1000);
	const lastFrameMs = frameDurationsMs.at(-1) ?? null;
	const averageFrameMs =
		frameDurationsMs.length > 0 ? frameDurationsMs.reduce((sum, frameDurationMs) => sum + frameDurationMs, 0) / frameDurationsMs.length : null;
	const maxFrameMs = frameDurationsMs.length > 0 ? Math.max(...frameDurationsMs) : null;
	const lastFrameTimestamp = frameTimestamps.at(-1);

	return {
		fps: timestampsWithinLastSecond.length,
		lastFrameMs,
		averageFrameMs,
		maxFrameMs,
		isIdle: lastFrameTimestamp === undefined ? true : now - lastFrameTimestamp > idleThresholdMs,
	};
};

export const formatRenderedDebugMetric = (value: number) => {
	if (value >= 1000) {
		const shortenedValue = value / 1000;

		return `${shortenedValue >= 100 ? Math.round(shortenedValue) : shortenedValue.toFixed(1)}k`;
	}

	return value.toString();
};

export const getRenderedDebugCountsLine = (snapshot: IRenderDebugSnapshot) => {
	const parts = [`Calls ${formatRenderedDebugMetric(snapshot.calls)}`, `Tris ${formatRenderedDebugMetric(snapshot.triangles)}`];

	if (snapshot.lines > 0) {
		parts.push(`Lines ${formatRenderedDebugMetric(snapshot.lines)}`);
	}

	if (snapshot.points > 0) {
		parts.push(`Points ${formatRenderedDebugMetric(snapshot.points)}`);
	}

	return parts.join(" | ");
};

export const notifyAboutInsufficientUpdatePermissionForSelectedItems = (spaceViewRenderer: SpaceViewRenderer) => {
	const {selectedItems} = spaceViewRenderer.spaceItemController;
	const itemsWithoutPermissionCount = spaceViewRenderer.actions.getNumberOfSpaceItemsWithoutPermission(selectedItems, Permission.Update);

	if (itemsWithoutPermissionCount === 0) {
		return;
	}

	notify(spaceViewRenderer.transport.appState.app.notificationContainer, {
		type: NotificationType.Warning,
		title: "Action not allowed!",
		description: `${itemsWithoutPermissionCount} of ${selectedItems.length} objects you have selected do not have the required permissions to perform this action. To obtain permission, contact your organization's administrator.`,
		lifeTime: Infinity,
	});
};

export const isSpaceNamePresentInAnotherSpaceVersion = (actions: AppActions, spaceName: string) => {
	const spaces = actions.getList<Space>(XyiconFeature.Space);

	return spaces.some((space: Space) => space.name === spaceName);
};

export const calculateSpaceResolution = (
	spaceUnitsPerMeter: number,
	insertionInfo: SpaceFileInsertionInfo,
	xyiconSize: IRealSize,
	mode: SpaceEditorMode,
) => {
	const spaceWidth = insertionInfo.width;
	const spaceHeight = insertionInfo.height;
	const correctionMultiplier = calculateCorrectionMultiplier(spaceUnitsPerMeter, insertionInfo, xyiconSize, mode);
	const spaceResolutionWidth = spaceWidth / correctionMultiplier;

	return {
		spaceResolution: {
			x: spaceResolutionWidth,
			y: spaceResolutionWidth / (spaceWidth / spaceHeight),
		},
		correctionMultiplier,
	};
};

export const calculateCorrectionMultiplier = (
	spaceUnitsPerMeter: number,
	insertionInfo: SpaceFileInsertionInfo,
	xyiconSize: IRealSize,
	mode: SpaceEditorMode,
) => {
	let spaceResolutionWidth: number = 0;

	if (mode === SpaceEditorMode.NORMAL) {
		const xyiconSizeInMeters = xyiconSize.value * Constants.DISTANCE_UNITS[xyiconSize.unit].multiplicator;
		const spaceWidthInMeters = (insertionInfo.width || Constants.DEFAULT_SPACE_RESOLUTION) / spaceUnitsPerMeter;

		spaceResolutionWidth = Constants.SIZE.XYICON * (spaceWidthInMeters / xyiconSizeInMeters);
	} else {
		spaceResolutionWidth = Constants.DEFAULT_SPACE_RESOLUTION;
	}

	return insertionInfo.width / spaceResolutionWidth;
};

export const getCorrectionMultiplierForSpaceItem = (spaceViewRenderer: SpaceViewRenderer, markup?: IMarkupMinimumData): number => {
	const modelDataSpaceId = markup?.spaceId;
	const modelDataSpace = spaceViewRenderer.actions.getSpaceById(modelDataSpaceId);
	return modelDataSpace && modelDataSpaceId !== spaceViewRenderer.space?.id
		? calculateCorrectionMultiplier(
				modelDataSpace.spaceUnitsPerMeter,
				modelDataSpace.selectedSpaceFile.insertionInfo,
				modelDataSpace.type.settings.xyiconSize,
				SpaceEditorMode.NORMAL,
			)
		: spaceViewRenderer.correctionMultiplier.current;
};

export const NameOfIconField = "Icon";

const getPeopleFieldDataTypes = (): FieldDataType[] => {
	return [FieldDataType.PeopleSingleChoiceList, FieldDataType.PeopleMultipleChoiceList];
};

const isPeopleField = (field: IFieldAdapter): boolean => {
	return getPeopleFieldDataTypes().includes(field.dataType);
};

export const isIconField = (field: IFieldAdapter): boolean => {
	return field && [XyiconFeature.Xyicon, XyiconFeature.XyiconCatalog].includes(field.feature) && field.refId.endsWith("/icon");
};

export const isCaptionSupportedForField = (field?: IFieldAdapter): boolean => {
	return field && !isIconField(field);
};

export const isReportSupportedForField = (field?: IFieldAdapter): boolean => {
	return field && !isIconField(field) && !isPeopleField(field);
};

export const isFilterSupportedForField = (field?: IFieldAdapter): boolean => {
	return field && !isIconField(field);
};

export const isConditionBasedSpotlightSupportedForField = (field?: IFieldAdapter): boolean => {
	return field && !isIconField(field);
};

export const isSpotlightSupportedForField = (field?: IFieldAdapter): boolean => {
	return field && !isIconField(field) && !isPeopleField(field);
};

export const isCardLayoutSupportedForField = (field?: IFieldAdapter): boolean => {
	return field && !isIconField(field) && !isPeopleField(field);
};

export const isSpaceItemCardSupportedForField = (field?: IFieldAdapter): boolean => {
	return field && !isIconField(field) && !isPeopleField(field);
};

export const getActiveCaptionFields = (feature: XyiconFeature, actions: AppActions) => {
	const captionFields: IFieldAdapter[] = [];

	const captionSettings = actions.getSelectedView(XyiconFeature.SpaceEditor).spaceEditorViewSettings.captions[
		feature === XyiconFeature.Xyicon ? "xyicon" : "boundary"
	];

	for (const checkedFieldRefId of captionSettings.checkList) {
		const field = actions.getFieldByRefId(checkedFieldRefId);

		if (field) {
			captionFields.push(field);
		}
	}

	return captionFields;
};

export const getFormattingColor = (item: IModel, fieldName: string, actions: AppActions) => {
	const modelData = item.ownFeature === XyiconFeature.Boundary ? (item as BoundarySpaceMap).parent : item;
	const field = actions.getFieldByName(item.ownFeature, fieldName);

	return actions.getFormattingColor(modelData, field?.refId);
};

export const getEmbeddedCounterPosition = (scale: Dimensions, correctionMultiplier: number, zOffset: number): Dimensions => {
	const offset = zOffset * correctionMultiplier;

	return {
		x: (scale.x - 32 * correctionMultiplier) / 2,
		y: scale.y / 2,
		z: scale.z + offset,
	};
};

export const getEmbeddedCounterScale = (xyiconMeshScale: number, correctionMultiplier: number) => {
	return (Constants.SIZE.COUNTER_HEIGHT * correctionMultiplier) / Math.abs(xyiconMeshScale);
};

export const getLinkIconPosition = (scale: Dimensions, correctionMultiplier: number, zOffset: number): Dimensions => {
	const offset = zOffset * correctionMultiplier;

	return {
		x: (scale.x - 32 * correctionMultiplier) / 2,
		y: (-scale.y + 29 * correctionMultiplier) / 2,
		z: scale.z + offset,
	};
};

export const getLockIconPosition = (
	scale: Dimensions,
	correctionMultiplier: number,
	zOffset: number,
	lockIconSizeInPx: number = Constants.SIZE.LOCK_ICON_PX,
): Dimensions => {
	const offset = zOffset * correctionMultiplier;
	const lockIconInsetRatio = lockIconSizeInPx / 32;

	return {
		x: (-scale.x + 7 * lockIconInsetRatio * correctionMultiplier) / 2,
		y: (-scale.y + 6 * lockIconInsetRatio * correctionMultiplier) / 2,
		z: scale.z + offset,
	};
};

export const getXyiconIndicatorPosition = (scale: Dimensions, correctionMultiplier: number): Dimensions => {
	const offset = 0.1 * correctionMultiplier;

	return {
		x: (-scale.x + 32 * correctionMultiplier) / 2,
		y: scale.y / 2,
		z: scale.z + offset,
	};
};

/**
 *
 * @param xyiconMeshScale usually 1, or -1 (if xyicon.isFlippedX is true). If the xyicon is temporary increased in size (eg.: another xyicon is hovered over), it can be larger as well
 */
export const getXyiconIndicatorScale = (xyiconMeshScale: number, correctionMultiplier: number) => {
	return (24 * correctionMultiplier) / Math.abs(xyiconMeshScale);
};

export const getXyiconHighlightScale = (scale: PointDouble) => {
	return Math.sqrt(scale.x * scale.x + scale.y * scale.y);
};

export const getBoundaryIndicatorScale = (correctionMultiplier: number) => {
	return 250 * correctionMultiplier;
};

// If the distance between the xyicon and its caption is larger than this, we show the leaderline
export const getCaptionLeaderLineVisibilityThreshold = (correctionMultiplier: number) => {
	return 70 * correctionMultiplier;
};

export const getDefaultPositionOfXyiconCaption = (
	xyiconPos: PointDouble,
	xyiconHeight: number,
	captionScale: PointDouble,
	correctionMultiplier: number,
): PointDouble => {
	return {
		x: xyiconPos.x,
		y: xyiconPos.y - (xyiconHeight + captionScale.y + 5 * correctionMultiplier) / 2,
	};
};

export const getDefaultPositionOfBoundaryCaption = (geometryData: PointDouble[]) => {
	return PolyLabel.getVisualCenter(geometryData);
};

export const setCaptionToDefaultPosition = (caption: ICaption, correctionMultiplier: number) => {
	const newDefaultPos =
		caption.parent.spaceItemType === "xyicon"
			? getDefaultPositionOfXyiconCaption(caption.parent.position, caption.parent.scale.y, caption.scale, correctionMultiplier)
			: getDefaultPositionOfBoundaryCaption(caption.parent.geometryData);

	caption.position.x = newDefaultPos.x;
	caption.position.y = newDefaultPos.y;
};

const _stepSize: number = 5; // px
const _directionsToCheck = [
	[0, 1],
	[1, 0],
	[-1, 0],
	[1, -1],
	[-1, -1],
	[1, 1],
	[-1, 1],
	[0, -1],
].map(VectorUtils.normalize);

/**
 * Returns the first object that rect1 has intersection with, or null if there's no intersection
 */
const detectCollisions = (rect1: IRect /* Caption */, arrayOfRects: IRect[]) => {
	for (const rect2 of arrayOfRects) {
		if (rect1 !== rect2) {
			if (THREEUtils.detectCollision(rect1, rect2)) {
				return rect2;
			}
		}
	}

	return null;
};

const solveCollisions = (caption: ICaption, arrayOfRects: IRect[], correctionMultiplier: number) => {
	let collisionRect = detectCollisions(caption, arrayOfRects);

	if (collisionRect) {
		let stepCount = 1;

		let isEmptySpotFound = false;
		let deltaX = 0;
		let deltaY = 0;

		const captionParentPosition = caption.parent.position;
		const parentScale = caption.parent.scale;

		// const parentBBox = caption.parent.spaceItemType === "xyicon" ? caption.parent.boundingBox : THREEUtils.calculateBox([captionParentPosition]);
		// const parentScale = THREEUtils.getSizeOfBoundingBox(parentBBox);

		const isXyicon = caption.parent.spaceItemType === "xyicon";

		while (!isEmptySpotFound) {
			for (const dir of _directionsToCheck) {
				if (isXyicon) {
					caption.position.x = captionParentPosition.x;
					caption.position.y = captionParentPosition.y;
				} else {
					setCaptionToDefaultPosition(caption, correctionMultiplier);
				}

				if (isXyicon && stepCount === 1 && (dir[0] === 0 || dir[1] === 0)) {
					deltaX = (dir[0] * (parentScale.x + caption.scale.x + 1 * correctionMultiplier)) / 2;
					deltaY = (dir[1] * (parentScale.y + caption.scale.y + 1 * correctionMultiplier)) / 2;
				} else {
					const stepSize =
						stepCount === 1
							? Math.sqrt((caption.scale.x + parentScale.x) ** 2 + (caption.scale.y + parentScale.y) ** 2) / 2 + 1 * correctionMultiplier
							: _stepSize;

					deltaX = dir[0] * stepCount * stepSize * correctionMultiplier;
					deltaY = dir[1] * stepCount * stepSize * correctionMultiplier;
				}

				caption.position.x += deltaX;
				caption.position.y += deltaY;

				collisionRect = detectCollisions(caption, arrayOfRects);

				if (collisionRect) {
					continue;
				} else {
					isEmptySpotFound = true;
					break;
				}
			}
			stepCount++;
		}
	}
};

export const getRadiusForCaptionCollision = (correctionMultiplier: number, captionFontSize: number) =>
	(5 * Constants.SIZE.XYICON * correctionMultiplier * captionFontSize) / Constants.SIZE.FONT.default;

export const updateCaption = (caption: ICaption, visibleXyicons: IRect[], visibleCaptions: ICaption[], correctionMultiplier: number) => {
	const rectangles = [...visibleXyicons, ...visibleCaptions];

	solveCollisions(caption, rectangles, correctionMultiplier);
};

export const getCaptionFontConfig = () => {
	return {
		opacity: 1,
		textHAlign: HorizontalAlignment.center,
		textVAlign: VerticalAlignment.center,
		isBold: true,
		isItalic: false,
		isUnderlined: false,
	};
};

const paddingTopBottomInPx: number = 10;

export const paddingLeftRightInPx: number = 15;

export const calculateCaptionSizeFromTextObject = (textObject: {size: PointDouble}, fontSize: number, correctionMultiplier: number) => {
	const ratio = fontSize / Constants.SIZE.FONT.default;
	const paddingLeftRight = paddingLeftRightInPx * correctionMultiplier * ratio;
	const paddingTopBottom = paddingTopBottomInPx * correctionMultiplier * ratio;

	return {
		x: Math.max(textObject.size.x + paddingLeftRight, 60 * correctionMultiplier * ratio),
		y: textObject.size.y + paddingTopBottom,
	};
};

export const getFeatureBySpaceItemType = (type: SpaceItemType) => {
	switch (type) {
		case "xyicon":
			return XyiconFeature.Xyicon;
		case "boundary":
			return XyiconFeature.Boundary;
		case "markup":
			return XyiconFeature.Markup;
	}
};

export enum MeasureType {
	NONE,
	DISTANCE,
	AREA,
}

export const MarkupSidePadding: number = 10; // px

export const notifyAboutViewFilteringOutItems = (items: ISpaceItemModel[], spaceViewRenderer: SpaceViewRenderer) => {
	spaceViewRenderer.itemsHiddenNotificationMaybe?.onClose(); // make sure only one of these notifications are shown at once
	const selectAndFocusItems = (items: ISpaceItemModel[]) => {
		spaceViewRenderer.inheritedMethods.selectItems(items, true);
		spaceViewRenderer.inheritedMethods.focusItems(items);
	};

	if (!items?.length) {
		console.warn(`Array is undefined, or empty. This shouldn't happen.`);
		return;
	}

	let itemLabel = "item";
	if (items.length > 1) {
		itemLabel += "s";
	}

	spaceViewRenderer.itemsHiddenNotificationMaybe = notify(spaceViewRenderer.transport.appState.app.notificationContainer, {
		lifeTime: Infinity,
		type: NotificationType.Warning,
		title: `${StringUtils.capitalize(itemLabel)} is not visible in the active view.`,
		description: `The view's filter prevents the ${itemLabel} from displaying on the space. To make the ${itemLabel} visible, switch to another view or edit the active filter. Click the Edit Details button to update the ${itemLabel}'${items.length > 1 ? "" : "s"} fields.`,
		buttonLabel: "Edit Details",
		onActionButtonClick: () => {
			selectAndFocusItems(
				items
					.filter((item) => {
						const item3DMaybe = spaceViewRenderer.getItemManager(item.ownFeature)?.getItemById(item.id);

						return item3DMaybe && !item3DMaybe.isDestroyed;
					})
					.map((item) => ((item as BoundarySpaceMap).isBoundarySpaceMap ? (item as BoundarySpaceMap).parent : item) as ISpaceItemModel),
			);
		},
	});

	return spaceViewRenderer.itemsHiddenNotificationMaybe;
};
